<template>
  <div>
    <div className="flex relative">
      <router-view name="sidebar" v-if="haveSidebar()" />
      <div :class="sidebarClass">
        <router-view name="header" v-if="haveHeader()" />
        <div v-if="isLoggedIn && isPricingUpdateEnabled" id="customerio-announcements" data-testid="customerio-announcements"></div>
        <div
          v-if="isLoggedIn && isPricingUpdateEnabled && showLimitAlert"
          class="bg-amber-50 dark:bg-amber-900/20 border-b border-amber-200 dark:border-amber-800 px-4 py-2.5 text-center text-sm"
          data-testid="limits-alert-bar"
        >
          <span class="text-amber-900 dark:text-amber-200">{{ limitAlertMessage }}</span>
          <a
            :href="upgradePath"
            class="font-semibold text-amber-700 dark:text-amber-300 hover:text-amber-800 dark:hover:text-amber-200 underline ml-1"
            data-testid="limits-alert-upgrade-link"
            >Upgrade for unlimited content, unlimited recording time, and more!</a
          >
        </div>
        <div :class="{ 'mb-4': !matchedAnnotations() }" id="main">
          <router-view />
        </div>
      </div>
    </div>
  </div>
</template>

<script lang="ts" setup>
import { storeToRefs } from 'pinia';
import { useLayoutStore } from '@/store/useLayoutStore';
import { useUserStore } from '@/store/useUserStore';
import { useRoute } from 'vue-router';
import { computed } from 'vue';
import { useLimits } from '@/composables/useLimits';

const { isSidebarExpanded } = storeToRefs(useLayoutStore());
const userStore = useUserStore();
const isLoggedIn = computed(() => userStore.isLoggedIn);
const { isPricingUpdateEnabled, showLimitAlert, limitAlertMessage } = useLimits();
const route = useRoute();

const upgradePath = '/plans?utm_source=web&utm_campaign=limit-bar';

const sidebarClass = computed(() => {
  const classes = ['w-full', 'sm:flex-grow'];
  if (matchedAnnotations()) {
    return classes.join(' ');
  }

  // add padding to the main content except for annotations
  classes.push('sm:px-6');
  if (!haveSidebar()) {
    return classes.join(' ');
  }

  if (isViewerPage()) {
    classes.push('min-w-0');
  }

  classes.push(isSidebarExpanded.value ? 'sm:ml-72' : collapsedSidebarMarginClass());
  return classes.join(' ');
});

const haveSidebar = (): boolean => route.matched.some(route => route.components && !!route.components.sidebar);
const haveHeader = (): boolean => route.matched.some(route => route.components && !!route.components.header);

// we want to render annotations full screen without padding or margin that causes scroll
const matchedAnnotations = (): boolean => route.matched.some(route => route.name === 'annotations');
const isViewerPage = (): boolean => route.matched.some(route => typeof route.name === 'string' && route.name.startsWith('viewer'));
const collapsedSidebarMarginClass = (): string => (isViewerPage() ? 'sm:ml-16' : 'sm:ml-24');
</script>
